/* eslint-disable react-refresh/only-export-components -- co-located helpers used only by sibling files within this feature; splitting them adds noise without value */
import clsx from 'clsx';

import type { DocumentStatus } from '../../api';

interface StatusBadgeProps {
  status: DocumentStatus | string;
  className?: string;
}

const STATUS_CLASS: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-700 border-slate-200',
  processing: 'bg-blue-100 text-blue-700 border-blue-200',
  approved: 'bg-success/10 text-success border-success/30',
  review_required: 'bg-warning/10 text-amber-800 border-warning/40',
  rejected: 'bg-danger/10 text-danger border-danger/30',
  failed: 'bg-slate-200 text-slate-700 border-slate-300',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  split: 'bg-primary/10 text-primary border-primary/30',
};

const STATUS_LABEL: Record<string, string> = {
  queued: 'En cola',
  processing: 'Procesando',
  approved: 'Aprobado',
  review_required: 'Requiere revisión',
  rejected: 'Rechazado',
  failed: 'Fallido',
  cancelled: 'Cancelado',
  split: 'Dividido',
};

export function StatusBadge({ status, className }: StatusBadgeProps): JSX.Element {
  const tone = STATUS_CLASS[status] ?? 'bg-slate-100 text-slate-700 border-slate-200';
  const label = STATUS_LABEL[status] ?? status;
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        tone,
        className,
      )}
    >
      {label}
    </span>
  );
}

/**
 * Marks a document whose retention window elapsed: the stored file was deleted and the extracted values
 * scrubbed. Rendered *alongside* the status badge rather than replacing it — expiry is an extra fact about
 * the document, and the original outcome (approved, rejected…) is still part of the audit record.
 */
export function ExpiredBadge({ className }: { className?: string }): JSX.Element {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        'border-slate-300 bg-slate-200 text-slate-600',
        className,
      )}
      title="Caducado por la política de retención: el archivo original y los datos extraídos ya no se conservan."
    >
      Caducado
    </span>
  );
}

/** Bucketed colour for a global score in [0, 1]. */
export function scoreToneClass(score: number | null | undefined): string {
  if (score === null || score === undefined) {
    return 'bg-slate-100 text-slate-700 border-slate-200';
  }
  if (score >= 0.9) {
    return 'bg-success/10 text-success border-success/30';
  }
  if (score >= 0.75) {
    return 'bg-warning/10 text-amber-800 border-warning/40';
  }
  return 'bg-danger/10 text-danger border-danger/30';
}

interface ScoreBadgeProps {
  score: number | null | undefined;
  className?: string;
}

export function ScoreBadge({ score, className }: ScoreBadgeProps): JSX.Element {
  const tone = scoreToneClass(score);
  const text =
    score === null || score === undefined ? '—' : `${(score * 100).toFixed(0)}%`;
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums',
        tone,
        className,
      )}
    >
      {text}
    </span>
  );
}
